import React, { useContext, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import { mediaQueryUp } from 'components/utils/breakpoint'
import BedbankOfferSummary from './BedbankOfferSummary'
import BedbankPopularFacilities from './BedbankPopularFacilities'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import { getShareModalConfig } from 'lib/getShareModalConfig'
import { connect } from 'react-redux'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import LineMapMarkerIcon from 'components/Luxkit/Icons/line/LineMapMarkerIcon'
import ShareModal from 'components/Common/Modals/ShareModal'
import TextLink from 'components/Luxkit/TextLink'
import LineEyeIcon from 'components/Luxkit/Icons/line/LineEyeIcon'
import {
  getOfferExtra,
  OFFER_VIEWS_24_HOURS_THRESHOLD,
} from 'selectors/offerExtraSelector'
import { fetchOfferExtraById } from 'actions/OfferActions'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import Subtitle from 'components/Luxkit/Typography/Subtitle'
import OfferDescription from 'components/OfferPage/OfferDescription'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import PromotionLabel from 'components/Luxkit/Label/PromotionLabel'
import LineUserIcon from 'components/Luxkit/Icons/line/LineUserIcon'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'
import { DAYS_TO_BOOK } from 'constants/bedbank'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import { showUserReviewsRating } from 'lib/reviewUtils'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import OfferPagePropertyTitle from '../OfferPagePropertyTitle'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import HeaderHeightContext from 'contexts/HeaderHeightContext'
import { useAppDispatch } from 'hooks/reduxHooks'
import Image from 'components/Common/Image/Image'
import PrimaryButtonPaletteProvider from 'contexts/PrimaryButtonPaletteProvider'

const RootContainer = styled(LayoutContainer)`
  padding-top: ${rem(32)};
  padding-bottom: ${rem(56)};
`

const SummaryArea = styled.div`
  display: none;

  ${mediaQueryUp.sm} {
    position: relative;
    align-self: stretch;
    display: block;
  }
`

const StickySummaryContent = styled.div`
  position: sticky;
  top: 0;
`

const Description = styled(MarkdownRender)`
  & > h4 + p {
    margin-top: ${rem(8)};
  }
`

const OfferViews = styled.div`
  display: flex;
  padding: ${rem(8)};
  justify-content: center;
  color: ${(props) => props.theme.palette.messaging.fast.main};
  background-color: ${(props) => props.theme.palette.messaging.fast.light};
`

interface Props {
  customerPortalURL: string;
  offer: App.BedbankOffer;
  regionCode: string;
  pathname: string;
  memberId: string;
  duration: number;
  checkinDate: moment.Moment;
  checkoutDate: moment.Moment;
  onViewPackageOptions?: () => void;
  rooms: Array<App.Occupants>;
  loadingPrices?: boolean;
  flightsEnabled: boolean;
  offerExtra: App.OfferExtra;
  greenCtaButtonEnabled?: boolean;
}

function BedbankOfferIntro(props: Props) {
  const {
    checkinDate,
    checkoutDate,
    customerPortalURL,
    memberId,
    offer,
    onViewPackageOptions,
    pathname,
    rooms,
    regionCode,
    loadingPrices,
    flightsEnabled,
    offerExtra,
    duration,
    greenCtaButtonEnabled,
  } = props

  const { heights } = useContext(HeaderHeightContext)

  const location = offer.property.locationTitleOverride ?
    offer.property.locationTitleOverride :
      [offer.property.address.city, offer.property.address.stateProvinceName]
        .filter(Boolean)
        .join(', ')
  const facilitiesOverride = offer.property?.facilitiesOverride

  const shareConfig = useMemo(
    () =>
      getShareModalConfig({
        customerPortalURL,
        image: offer.images[0],
        memberId,
        offerId: offer.id,
        offerLocation: location,
        pathname,
        region: regionCode,
        type: 'bedbankOffer',
        utmCampaign: 'webshare',
      }),
    [
      customerPortalURL,
      location,
      memberId,
      offer.id,
      offer.images,
      pathname,
      regionCode,
    ],
  )

  const urgencyToday = useMemo(() => {
    if (offerExtra) {
      const views =
        offerExtra.viewsInLast24Hours > 0 ?
          offerExtra.viewsInLast24Hours :
          offerExtra.viewsToday
      return {
        isActive: views >= OFFER_VIEWS_24_HOURS_THRESHOLD,
        captionText: `${views} people viewed this offer today`,
        Icon: LineEyeIcon,
      }
    }
    return { isActive: false }
  }, [offerExtra])

  const { expandableDesc, nonExpandableDesc } = useMemo(() => {
    const index = offer.description.indexOf('####')
    const mainDesc = offer.description.substring(0, index)
    const additionalDesc = offer.description.slice(index)

    if (mainDesc.length > 50 && additionalDesc) {
      const expandableDesc = mainDesc + '\n' + additionalDesc
      return { expandableDesc, nonExpandableDesc: null }
    } else {
      return { expandableDesc: null, nonExpandableDesc: offer.description }
    }
  }, [offer.description])

  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(fetchOfferExtraById(offer.id))
  }, [dispatch, offer.id])

  const hasPromotions = offer.promotions.length > 0

  const bedBankOfferPromotionEndDate = offer.promotions[0]?.periodSchedule.to

  return (
    <RootContainer>
      <PrimaryButtonPaletteProvider palette={greenCtaButtonEnabled ? 'abTestGreen' : 'default'}>
        <Group direction="vertical" gap={16}>
          <Group
          direction="vertical"
          smDirection="horizontal-reverse"
          horizontalAlign="space-between"
          verticalAlign="center"
          gap={4}
        >
            <Group
            direction="horizontal"
            gap={8}
            verticalAlign="center"
            horizontalAlign="end"
          >
              <TripGuard>
                <ShareModal shareConfig={shareConfig}>
                  <ShareWithFriendsButton />
                </ShareModal>
                <BookmarkButton offer={offer} />
              </TripGuard>

              <TripGuard inverse>
                <ShareModal shareConfig={shareConfig}>
                  <ShareWithFriendsButton outdent="right" />
                </ShareModal>
                <BookmarkButton offer={offer} />
              </TripGuard>
            </Group>

            <LabelGroup>
              {hasPromotions && (
                <ProductTypeLabel productType="limited_time_special" />
              )}

              {hasPromotions && bedBankOfferPromotionEndDate && (
                <OfferUrgencyCountdownLabel
                endDate={bedBankOfferPromotionEndDate}
                daysToBook={DAYS_TO_BOOK}
              />
              )}

              {regionCode !== 'IN' && (
                <PromotionLabel variant="default" icon={<LineUserIcon />}>
                  24/7 support
                </PromotionLabel>
              )}
            </LabelGroup>
          </Group>

          <Group direction="horizontal" gap={48}>
            <div>
              {urgencyToday.isActive && (
                <CSSBreakpoint max="xs">
                  <OfferViews>
                    <CaptionTextBlock
                    variant="medium"
                    weight="bold"
                    startIcon={<urgencyToday.Icon />}
                  >
                      {urgencyToday.captionText}
                    </CaptionTextBlock>
                  </OfferViews>
                </CSSBreakpoint>
              )}
              <Group direction="vertical" horizontalAlign="start" gap={16}>
                <Group direction="vertical" gap={8}>
                  <Group
                  fullWidth
                  direction="horizontal"
                  horizontalAlign="space-between"
                  verticalAlign="center"
                  gap={8}
                >
                    <Group direction="vertical" gap={4}>
                      {(offer.property.locationTitleOverride ||
                      offer.property.address) && (
                        <BodyTextBlock
                          data-testid="location-box"
                          variant="medium"
                          weight="bold"
                          startIcon={<LineMapMarkerIcon />}
                        >
                          {location} •{' '}
                          <TextLink href="#getting-there">view map</TextLink>
                        </BodyTextBlock>
                      )}

                      <OfferPagePropertyTitle
                      propertyName={offer.name}
                      data-testid="bedbank-offer-name"
                    />

                      {offer.tagline && (
                        <Subtitle variant="subtitle2" as="h2">
                          {offer.tagline}
                        </Subtitle>
                      )}
                    </Group>
                    {offer.logoImageId && (
                      <CSSBreakpoint min="md">
                        <Image
                        id={offer.logoImageId}
                        dpr={3}
                        height={75}
                        enhancedFormats={false}
                        alt={`${offer.property.name || 'Vendor'} logo`}
                      />
                      </CSSBreakpoint>
                    )}
                  </Group>

                  {!hasPromotions && (
                    <div>
                      <ProductTypeLabel productType="bedbank_hotel" />
                    </div>
                  )}

                  {showUserReviewsRating(
                    offer.property.rating,
                    offer.property.reviewsTotal,
                  ) && (
                    <NumberRating
                      link
                      inlineLabel
                      variant="small"
                      total={offer.property.reviewsTotal}
                      rating={offer.property.rating}
                      ratingSubtitle={
                        offer.property.reviewsSource === 'google' &&
                        'From online reviews'
                      }
                    />
                  )}
                </Group>
                <Group direction="vertical" gap={24}>
                  {expandableDesc && (
                    <OfferDescription description={expandableDesc} />
                  )}
                  {nonExpandableDesc && (
                    <Description type="compact" content={nonExpandableDesc} />
                  )}
                  {hasPromotions && offer.additionalPromoContent && (
                    <MarkdownRender
                    type="compact"
                    content={offer.additionalPromoContent}
                  />
                  )}
                  {!hasPromotions && !facilitiesOverride && (
                    <BedbankPopularFacilities offer={offer} />
                  )}
                </Group>
              </Group>
            </div>

            <SummaryArea>
              <StickySummaryContent
              style={{
                top: rem(heights.bedbankOfferHeader + 16),
              }}
            >
                {urgencyToday.isActive && (
                  <OfferViews>
                    <CaptionTextBlock
                    variant="medium"
                    weight="bold"
                    startIcon={<urgencyToday.Icon />}
                  >
                      {urgencyToday.captionText}
                    </CaptionTextBlock>
                  </OfferViews>
                )}
                <BedbankOfferSummary
                loadingPrices={loadingPrices}
                onViewPackageOptions={onViewPackageOptions}
                checkinDate={checkinDate}
                checkoutDate={checkoutDate}
                duration={duration}
                offer={offer}
                rooms={rooms}
                flightsEnabled={flightsEnabled}
              />
              </StickySummaryContent>
            </SummaryArea>
          </Group>
        </Group>
      </PrimaryButtonPaletteProvider>
    </RootContainer>
  )
}

function mapStateToProps(state: App.State, ownProps: Partial<Props>) {
  return {
    regionCode: state.geo.currentRegionCode,
    pathname: state.router.location.pathname,
    customerPortalURL: state.config.customerPortalURL,
    memberId: state.auth.account.memberId,
    loadingPrices: !!state.offer.offerRatesLoading[ownProps.offer.id],
    offerExtra: getOfferExtra(state, ownProps.offer?.id),
  }
}

export default connect(mapStateToProps)(BedbankOfferIntro)
